import React, { useState, useEffect } from 'react'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { useSelector } from 'react-redux'
import { MenuItem } from '@mui/material'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'

import { useParams } from 'react-router-dom'

import { useTranslation } from 'react-i18next'
import '../../i18n'
import NumberInput from '../../components/common/NumberInput'
import { useNavigate } from 'react-router-dom'

import {
  ExistingControl,
  IncidentPerimeter,
} from '../../types/existingControlTypes'
import {
  getExistingControlbyId,
  updateExistingControl,
} from '../../redux/existingControls/existingControlActions'
import UseAlert from '../../components/common/alert'
import SelectcontrolNature from '../../components/common/ControlNature'
import { translatePerimeter } from '../../helpers/helper'
import i18n from '../../i18n'
import {
  FormAside,
  FormField,
  FormFoot,
  FormGrid,
  FormLayout,
  FormPageHead,
  FormSection,
  FormShell,
} from '../../components/common/form'

export default function ExistingControlUpdate() {
  const [ExistingControl, setExistingControl] = useState<ExistingControl>({
    Label: '',
    Description: '',
    Perimetre: '',
    NombreActifCouvert: 0,
    TauxCouverture: 0,
    ControlNature: 'Autres',
    SecurityControlId: '',
  })
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    })

    window.history.back()
  }
  const dispatch: AppDispatch = useAppDispatch()
  const { t } = useTranslation()

  const existingControlsList = useSelector(
    (state: RootState) => state.existingControl.existingControl
  )
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  useEffect(() => {
    if (id) {
      dispatch(getExistingControlbyId(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (existingControlsList !== null) {
      setExistingControl({
        _id: existingControlsList._id,

        ExistingControlId: existingControlsList.ExistingControlId,

        Label: existingControlsList.Label,
        Description: existingControlsList.Description,
        Perimetre: existingControlsList.Perimetre,
        NombreActifCouvert: existingControlsList.NombreActifCouvert,
        TauxCouverture: existingControlsList.TauxCouverture,
        ControlNature: existingControlsList.ControlNature,
        SecurityControlId: existingControlsList.SecurityControlId,
      })
    }
  }, [existingControlsList, id])
  const handleInputChange = (
    event:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = event.target
    setExistingControl((prevExistingControl) => ({
      ...prevExistingControl,
      [name]: value,
    }))
  }

  const handleUpdateExistingControl = async () => {
    try {
      if (ExistingControl.Label && ExistingControl._id) {
        await dispatch(
          updateExistingControl({
            updatedExistingControl: ExistingControl,
            _id: ExistingControl._id,
          })
        )
        setAlert({
          open: true,
          message: t('alerts.existingControl.updateSuccess'),
          severity: 'success',
        })
      } else {
        setAlert({
          open: true,
          message: t('alerts.existingControl.emptyFields'),
          severity: 'error',
        })
      }
    } catch (error: any) {
      console.error('Error updating mission:', error.message)

      setAlert({
        open: true,
        message: t('alerts.existingControl.updateError'),
        severity: 'error',
      })
    }
  }
  const lang = i18n.language

  const handlePerimetreChange = (event: SelectChangeEvent<string>) => {
    setExistingControl((prev) => ({
      ...prev,
      Perimetre: event.target.value,
    }))
  }
  const submitLabel = t('buttons.update')
  const backPath = ExistingControl.SecurityControlId
    ? `/security-existing/${ExistingControl.SecurityControlId}`
    : '/existing'

  return (
    <div className="page-content">
      <FormPageHead
        eyebrow={t('Existing Controls')}
        title={`${t('buttons.update')} ${ExistingControl.Label || ''}`.trim()}
        subtitle={t('existingControlForm.updateSubtitle', {
          defaultValue:
            'Update the control description, scope, nature and coverage.',
        })}
        backLabel={t('Back', { defaultValue: 'Back to controls' })}
        onBack={() => navigate(backPath)}
      />
      <UseAlert open={alert.open} message={alert.message} severity={alert.severity} onClose={handleCloseAlert} />
      <FormLayout
        main={
          <FormShell>
            <FormSection title={t('Control details', { defaultValue: 'Control details' })}>
              <FormGrid cols={2}>
                <FormField label={t('label')} required>
                  <input className="incyia-form-inp" name="Label" value={ExistingControl.Label} onChange={handleInputChange} />
                </FormField>
                <FormField label={t('Perimeter')} required>
                  <Select className="incyia-form-inp" value={ExistingControl.Perimetre} onChange={handlePerimetreChange}>
                    <MenuItem value="" disabled />
                    {Object.entries(IncidentPerimeter).map(([key, value]) => (
                      <MenuItem key={key} value={key}>{key ? translatePerimeter(key, lang) : value}</MenuItem>
                    ))}
                  </Select>
                </FormField>
                <FormField label={t('Description')} required span>
                  <textarea className="incyia-form-inp" name="Description" value={ExistingControl.Description} onChange={handleInputChange} rows={4} />
                </FormField>
              </FormGrid>
            </FormSection>
            <FormSection title={t('Coverage', { defaultValue: 'Coverage' })}>
              <FormGrid cols={3}>
                <FormField label={t('Control Nature')}>
                  <SelectcontrolNature value={ExistingControl.ControlNature} onChange={handleInputChange} />
                </FormField>
                <FormField label={t('Number of Active Covered')}>
                  <NumberInput decimals={0} min={0} name="NombreActifCouvert" value={ExistingControl.NombreActifCouvert} onChange={(v) => setExistingControl((prev) => ({ ...prev, NombreActifCouvert: v ?? 0 }))} />
                </FormField>
                <FormField label={t('Coverage Rate')}>
                  <NumberInput min={0} max={100} adornment="%" name="TauxCouverture" value={ExistingControl.TauxCouverture} onChange={(v) => setExistingControl((prev) => ({ ...prev, TauxCouverture: v ?? 0 }))} />
                </FormField>
              </FormGrid>
            </FormSection>
            <FormFoot
              note={t('actionCreate.aside.requiredMark', { defaultValue: '* Required' })}
              cancelLabel={t('Cancel')}
              submitLabel={submitLabel}
              onCancel={() => navigate(backPath)}
              onSubmit={handleUpdateExistingControl}
            />
          </FormShell>
        }
        aside={
          <FormAside
            title={t('actionCreate.aside.inThisForm', { defaultValue: 'In this form' })}
            steps={[
              { title: t('Control details', { defaultValue: 'Control details' }), fieldCount: 3 },
              { title: t('Coverage', { defaultValue: 'Coverage' }), fieldCount: 3 },
            ]}
            requiredCount={2}
            submitLabel={submitLabel}
            tipTitle={t('actionCreate.aside.goodToKnow', { defaultValue: 'Good to know' })}
            tip={t('existingControlForm.tip', { defaultValue: 'Coverage information helps assess how completely this control protects the selected scope.' })}
            fieldsLabel={(n) => `${n} ${n === 1 ? 'field' : 'fields'}`}
            requiredNote={(n, submit) => `${n} required before you can ${submit}.`}
          />
        }
      />
    </div>
  )
}
